'use client'

import { useIsHydrated, useMandatoryContext } from '@repo/hooks'
import {
  createContext,
  type Dispatch,
  type MouseEvent,
  type PropsWithChildren,
  type SetStateAction,
  useState
} from 'react'

import { useIsLgUp, useIsMedium } from '@/hooks/useBreakpoints'

export type BorrowDashboardMode = 'loan' | 'collateral'

export interface DashboardContextProps {
  isDashboardExpanded: boolean
  isDashboardHovered: boolean
  isSmallDashboard: boolean
  borrowDashboardMode: BorrowDashboardMode
  toggleDashboard: () => void
  onClickToggleDashboard: (e: MouseEvent<HTMLElement>) => void
  setIsDashboardHovered: (hovered: boolean) => void
  setBorrowDashboardMode: Dispatch<SetStateAction<BorrowDashboardMode>>
}

export const DashboardContext = createContext<DashboardContextProps | null>(null)

export enum DashboardClass {
  PREVENT_DASHBOARD_CARD_HOVER_CLASS = 'prevent-dashboard-card-hover',
  DASHBOARD_CARD_BUTTON_EXPAND_CLASS = 'dashboard-card-button-expand'
}

export const useDashboardContext = () => {
  return useMandatoryContext(DashboardContext, 'DashboardContext')
}

export type DashboardContextProviderProps = PropsWithChildren

const useDashboardHook = ({ isSmallDashboard }: { isSmallDashboard: boolean }) => {
  const [isDashboardExpandedState, setIsDashboardExpanded] = useState(false)
  const [isDashboardHovered, setIsDashboardHovered] = useState(false)

  const isDashboardExpanded = isSmallDashboard ? false : isDashboardExpandedState

  const toggleDashboard = () => {
    setIsDashboardExpanded(prev => !prev)
  }

  const onClickToggleDashboard = (e: MouseEvent<HTMLElement>) => {
    if (
      // oxlint-disable-next-line typescript/consistent-type-assertions
      (e?.target as HTMLElement)?.closest(`.${DashboardClass.PREVENT_DASHBOARD_CARD_HOVER_CLASS}`)
    )
      return
    toggleDashboard()
  }

  return {
    isDashboardExpanded,
    isDashboardHovered,
    toggleDashboard,
    setIsDashboardHovered,
    onClickToggleDashboard
  }
}

const DashboardContextValueProvider = ({
  isSmallDashboard,
  children
}: PropsWithChildren<{ isSmallDashboard: boolean }>) => {
  const [borrowDashboardMode, setBorrowDashboardMode] = useState<BorrowDashboardMode>('loan')

  const dashboard = useDashboardHook({ isSmallDashboard })

  const value = {
    isSmallDashboard,
    borrowDashboardMode,
    setBorrowDashboardMode,
    ...dashboard
  }

  return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>
}

const DashboardContextProvider = ({ children }: DashboardContextProviderProps) => {
  const isSmallDashboard = useIsMedium()

  return (
    <DashboardContextValueProvider isSmallDashboard={isSmallDashboard}>
      {children}
    </DashboardContextValueProvider>
  )
}

/**
 * The server can't know the viewport, so the server render and hydration render
 * `children` in both layouts and `lg:` picks one (= `useIsLgUp`'s 1024px
 * cutoff). After hydration only the slot matching the media query keeps its
 * children, so the visible tree stays mounted and nothing moves. Wrap only a
 * small subtree: it is rendered twice until hydration.
 */
export const ResponsiveDashboardContextProvider = ({ children }: DashboardContextProviderProps) => {
  const isMedium = !useIsLgUp()
  const isHydrated = useIsHydrated()

  return (
    <>
      <div className={isHydrated ? (isMedium ? 'hidden' : undefined) : 'max-lg:hidden'}>
        {(!isHydrated || !isMedium) && (
          <DashboardContextValueProvider isSmallDashboard={false}>
            {children}
          </DashboardContextValueProvider>
        )}
      </div>
      <div className={isHydrated ? (isMedium ? undefined : 'hidden') : 'lg:hidden'}>
        {(!isHydrated || isMedium) && (
          <DashboardContextValueProvider isSmallDashboard>{children}</DashboardContextValueProvider>
        )}
      </div>
    </>
  )
}

export default DashboardContextProvider
